/**
 * Regular expression to match quoted text in a query string.
 * Matches text wrapped in <q> tags, capturing the quoted content.
 */
export const QUOTE_REGEX = /<q>(.*?)<\/q>/s;
const FOLLOW_UP_REGEX = /<q>\n(.*?)\n<\/q>/gs;
const FOLLOW_UP_LIST_REGEX = /<q>\n\n(.*?)\n\n<\/q>/s;
const FOLLOW_UP_DELIMITER_REGEX = /<(\\*)\/q>/g;
const ESCAPED_FOLLOW_UP_DELIMITER_REGEX = /<\\(\\*)\/q>/g;
/**
 * Extracts a quote and the actual query from a query string.
 *
 * @param queryString - The original query string that may contain a quote
 * @returns An object with the extracted quote and the actual query
 */
export function extractQuoteFromQuery(queryString) {
    if (!queryString) {
        return { quote: null, actualQuery: null };
    }
    const followUps = parseFollowUps(queryString);
    if (followUps) {
        return {
            quote: formatFollowUps(followUps),
            actualQuery: queryString.replace(FOLLOW_UP_REGEX, '').trim(),
        };
    }
    const hasQuote = QUOTE_REGEX.test(queryString);
    if (!hasQuote) {
        return { quote: null, actualQuery: queryString };
    }
    const quoteMatch = queryString.match(QUOTE_REGEX);
    const quote = quoteMatch?.[1] || null;
    const actualQuery = queryString.replace(QUOTE_REGEX, '').trim();
    return { quote, actualQuery };
}
/**
 * Adds a quote to a query string.
 *
 * @param query - The query string
 * @param quote - The text to be quoted
 * @returns A new query string with the quote embedded in <q> tags
 */
export function getQueryWithQuote(query, quote) {
    const trimmedQuote = quote.trim();
    if (!trimmedQuote) {
        return query;
    }
    if (parseFollowUps(trimmedQuote)) {
        return `${trimmedQuote} ${query.replace(new RegExp(QUOTE_REGEX, 'gs'), '').trim()}`.trim();
    }
    // If query already has a quote, replace it
    if (QUOTE_REGEX.test(query)) {
        return query.replace(QUOTE_REGEX, `<q>${trimmedQuote}</q>`);
    }
    // If the query is empty, return the quote
    if (query.length === 0) {
        return trimmedQuote;
    }
    // Otherwise, add the quote at the beginning
    return `<q>${trimmedQuote}</q> ${query}`;
}
export function formatFollowUps(selections) {
    if (selections.length === 0)
        return null;
    if (selections.length === 1)
        return selections[0]?.text ?? null;
    return selections.map(({ text }, index) => `${index + 1}. ${text}`).join('\n\n');
}
// Older clients only remove one <q> block. Quote-prefixed lines preserve passage
// boundaries within that block while remaining readable to those clients.
export function serializeFollowUps(selections) {
    if (!selections.length)
        return null;
    const passages = selections
        .map(({ text }) => `> ${text.replace(FOLLOW_UP_DELIMITER_REGEX, '<\\$1/q>').replaceAll('\n', '\n> ')}`)
        .join('\n\n');
    return `<q>\n\n${passages}\n\n</q>`;
}
export function parseFollowUps(value) {
    if (!value)
        return null;
    const list = value.match(FOLLOW_UP_LIST_REGEX);
    const passages = list
        ? list[1].split('\n\n').map((passage) => {
            const lines = passage.split('\n');
            return lines.every((line) => line.startsWith('> '))
                ? lines.map((line) => line.slice(2)).join('\n')
                : null;
        })
        : [...value.matchAll(FOLLOW_UP_REGEX)].map((match) => match[1]);
    return passages.length && passages.every((text) => text !== null)
        ? passages.map((text, index) => ({
            id: String(index),
            text: text.replace(ESCAPED_FOLLOW_UP_DELIMITER_REGEX, '<$1/q>'),
        }))
        : null;
}
//# sourceMappingURL=quoteUtils.js.map